import React, { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Divider, Tabs } from '@Sage/hoi-poi-ui';

import { CAMPAIGNS_MARKETING } from 'constants/Entities';
import { ensureRoute } from 'utils/routes';
import CampaignDetailsHeader from './CampaignDetailsHeader';
import CampaignRecipientsPanel from './CampaignRecipientsPanel';

import { getLiteral } from 'utils/getLiteral';

import './styles.scss';

const TAB_SUMMARY = 'summary';
const TAB_RECIPIENTS = 'recipients';

/**
 * Marketing campaign detail body (list row or entity detail). Summary placeholder;
 * Recipients loads mailing event types and paginated tables.
 */
const CampaignDetails = memo(({ campaign, initialTabKey }) => {
    const activeKey = initialTabKey === TAB_RECIPIENTS ? TAB_RECIPIENTS : TAB_SUMMARY;

    const handleTabChange = useCallback(
        (key) => {
            if (!campaign?.id) {
                return;
            }

            const route =
                key === TAB_RECIPIENTS
                    ? `${CAMPAIGNS_MARKETING.route}/${campaign.id}/${TAB_RECIPIENTS}`
                    : `${CAMPAIGNS_MARKETING.route}/${campaign.id}`;

            ensureRoute(route);
        },
        [campaign?.id],
    );

    const recipientsContent = useMemo(
        () => (
            <div className="fm-campaign-details__tab-pane fm-campaign-details__tab-pane--recipients">
                <CampaignRecipientsPanel
                    campaignId={campaign?.id}
                    isActive={activeKey === TAB_RECIPIENTS}
                />
            </div>
        ),
        [campaign?.id, activeKey],
    );

    const tabs = useMemo(
        () => [
            {
                key: TAB_SUMMARY,
                title: getLiteral('label_summary'),
                content: <div className="fm-campaign-details__tab-pane" />,
            },
            {
                key: TAB_RECIPIENTS,
                title: getLiteral('label_recipients'),
                content: recipientsContent,
            },
        ],
        [recipientsContent],
    );

    return (
        <div className="fm-campaign-details">
            <CampaignDetailsHeader campaign={campaign} />
            <Divider className="fm-campaign-details__divider" />
            <div className="fm-campaign-details__tabs-wrap">
                <Tabs
                    className="fm-campaign-details__tabs"
                    activeKey={activeKey}
                    onChange={handleTabChange}
                    tabs={tabs}
                    editable={false}
                />
            </div>
        </div>
    );
});

CampaignDetails.displayName = 'CampaignDetails';

CampaignDetails.propTypes = {
    campaign: PropTypes.object,
    initialTabKey: PropTypes.oneOf([TAB_SUMMARY, TAB_RECIPIENTS]),
};

export default CampaignDetails;
